import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ViewChild,
  ElementRef,
  HostListener,
  Inject,
  Renderer2,
  DestroyRef
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router, Data } from '@angular/router';
import { DOCUMENT, LocationStrategy } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { IconComponent, arrowLeft, cross, provideIcons } from '../../shared/icons';
import { MapComponent } from '@shared/components';
import { HotelSelectionComponent } from '../../shared/saleslocation/shop-sort/hotel-selection/hotel-selection.component';
import { HotelComponent } from '../../shared/saleslocation/shop-sort/hotel/hotel.component';
import { SelectedHotelService } from '../../shared/services/selected-hotel.service';
import { AbTestVersionDirective } from '@ab-test';
import { eventTimeType, ShopService } from '../../shared/services/shop.service';
import { ShopInfoService } from '../../core/services/shop-info.service';
import { MapService } from '../../shared/services/map.service';
import { FLY_IN_OUT_ANIMATION_Y } from '../../core/animations';
import { Shop } from '../../shared/entities/shop';
import { ResolutionService } from '../../core/services/resolution.service';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { AlpitranslatePipe } from '../../core/pipes/alpitranslate.pipe';
import { CrowdinTranslationService } from '../../shared/services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

@Component({
  selector: 'app-map-modal',
  templateUrl: './map-modal.component.html',
  styleUrls: ['./map-modal.component.scss'],
  animations: [FLY_IN_OUT_ANIMATION_Y],
  imports: [
    IconComponent,
    MapComponent,
    AlpitranslatePipe,
    // lazy imports
    HotelSelectionComponent,
    HotelComponent,
    AbTestVersionDirective
  ],
  providers: [provideIcons({ cross, arrowLeft })]
})
export class MapModalComponent implements OnInit, OnDestroy {
  @HostBinding('@flyInOutTrigger') flyInOutTrigger = 'in';
  @ViewChild('modal') modalWindow: ElementRef;
  @ViewChild('map', { static: false }) map: MapComponent;

  shops: Array<Shop>;
  private shopId: string;
  private prevCanonicalLink: string;
  isMobile = true;
  mapType = 'town';

  protected showHotelSearch = false;

  constructor(
    protected shopService: ShopService,
    protected shopInfoService: ShopInfoService,
    protected mapService: MapService,
    protected resolutionService: ResolutionService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected locationStrategy: LocationStrategy,
    @Inject(DOCUMENT) protected document: Document,
    protected platformService: PlatformInformationService,
    protected selectedHotelService: SelectedHotelService,
    protected renderer2: Renderer2,
    private destroyRef: DestroyRef,
    private crowdinTranslationService: CrowdinTranslationService
  ) {}

  ngOnInit() {
    this.crowdinTranslationService.watchLangChange({ loaderMap: loaderMap, destroyRef: this.destroyRef });
    this.injectCanonicalLink();
    this.shops = this.shopService.currentShops;
    this.shops.forEach(shop => {
      shop.lazyshow = true;
    });
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.updateEnv());
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.updateMap());
    this.shopService.updates
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event.type === eventTimeType.onShopUpdate)
      )
      .subscribe(this.initMap.bind(this));
    this.mapService.onCloseMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.closeMe();
    });
    this.isMobile = this.resolutionService.isMobileDevice();
    this.resolutionService.sizeReferenceChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.resolutionChange());
  }

  changeHotelSelection(selectedHotel?: google.maps.places.PlaceResult) {
    this.showHotelSearch = false;
    if (!selectedHotel) {
      this.shops.forEach(shop => {
        shop.hotelDistance = null;
      });
    }
    this.selectedHotelService.setHotel(selectedHotel);

    this.centerOnHotel();
  }

  centerOnHotel(hotel = this.selectedHotelService.selectedHotel()) {
    if (hotel) {
      this.map.updateCenterpoint({
        latitude: hotel.geometry.location.lat(),
        longitude: hotel.geometry.location.lng(),
        address: ''
      });
    }
  }

  /**
   * init the map with shops if loading occured later or if new shops got loaded. If there's an shop id in
   * the url params, try to zoom the map on this location
   */
  initMap(event: { type: eventTimeType; data: { shops: Array<Shop> } }) {
    this.shops = event.data.shops;
    if (this.shopId) {
      setTimeout(() => {
        const shop = event.data.shops.find(_shop => _shop.id.toString() === this.shopId);
        if (shop) {
          this.mapService.changedPoint(shop.location);
        } else {
          // in the mapPreview: don't select a shop, just center into the middle of all shops
          this.mapService.changedPoint(event.data.shops[0].location);
        }
      }, 100);
    } else if (this.selectedHotelService.selectedHotel()) {
      setTimeout(() => {
        this.mapService.changedPoint({
          latitude: +this.selectedHotelService.selectedHotel().geometry.location.lat(),
          longitude: +this.selectedHotelService.selectedHotel().geometry.location.lng(),
          address: ''
        });
      }, 100);
    }
  }

  updateEnv() {
    return (data: Data) => {
      if (data.mapType) {
        this.mapType = data.mapType;
      }
    };
  }

  /**
   * will update the map location on url changes
   */
  updateMap() {
    return (params: ParamMap) => {
      if (params.has('shopId')) {
        this.shopId = params.get('shopId');
        const shop = this.shopService.currentShops.find(_shop => _shop.id.toString() === this.shopId);
        if (shop) {
          this.mapService.changedPoint(shop.location);
        } else {
          this.shopInfoService.getLocationById(params.get('shopId')).subscribe({
            next: _shop => {
              this.shops = [_shop];
              setTimeout(() => {
                this.mapService.changedPoint(_shop.location);
              }, 100);
            },
            error: e => {
              console.error(e);
            }
          });
        }
      } else {
        this.shopId = null;
        if (this.selectedHotelService.selectedHotel()) {
          this.mapService.changedPoint({
            latitude: +this.selectedHotelService.selectedHotel().geometry.location.lat(),
            longitude: +this.selectedHotelService.selectedHotel().geometry.location.lng(),
            address: ''
          });
        } else if (this.shops?.length) {
          // in the mapPreview: don't select a shop, just center into the middle of all shops
          this.mapService.changedPoint(this.shops[0].location);
        }
      }
    };
  }

  closeMe() {
    this.router.navigate([{ outlets: { modal: null } }], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'merge'
    });
  }

  resolutionChange() {
    return () => {
      this.isMobile = this.resolutionService.isMobileDevice();
      this.mapService.changedPoint(this.mapService.lastPoint);
    };
  }

  ngOnDestroy() {
    this.removeCanonicalLink();
  }

  clickOutside(target) {
    if (!this.modalWindow.nativeElement.contains(target)) {
      // TODO: is this on click nav considered outside ngZone?
      // Navigation triggered outside Angular zone, did you forget to call 'ngZone.run()'?
      this.closeMe();
    }
  }

  /**
   * sets a canonical link to the parents' route without the modal.
   * Will remember an already existing canonical link to set again once the modal is closed with
   * removeCanonicalLink()
   */
  private injectCanonicalLink() {
    // make sure there's only ONE canonical link
    // TODO: there should be a canonical-link service that allows you to set/update and remove a canonical link
    const prev = this.document.getElementById('canonical') as HTMLLinkElement;
    const link =
      this.platformService.getHostname() +
      this.locationStrategy.prepareExternalUrl(
        this.router.serializeUrl(
          this.router.createUrlTree([{ outlets: { modal: null } }], { relativeTo: this.route.parent })
        )
      );
    if (prev) {
      // update existing canonical link
      this.prevCanonicalLink = prev.href;
      prev.href = link;
    } else {
      // create a canonical link
      const el = this.renderer2.createElement('link');
      el.rel = 'canonical';
      el.id = 'canonical';
      el.href = link;
      this.renderer2.appendChild(this.document.head, el);
    }
  }

  private removeCanonicalLink() {
    const el = this.document.getElementById('canonical') as HTMLLinkElement;
    if (el) {
      if (this.prevCanonicalLink?.length) {
        el.href = this.prevCanonicalLink;
      } else {
        this.renderer2.removeChild(this.document.head, el);
      }
    }
  }

  @HostListener('document:mousedown', ['$event.target']) mousedown(target) {
    this.clickOutside(target);
  }
  @HostListener('document:touchstart', ['$event.target']) touchdown(target) {
    this.clickOutside(target);
  }
}
